import { convertToWebP, toSupabaseTransformableUrl } from "../imageUtils";
import {
  BANNER_EXPORT_WIDTH,
  BANNER_DISPLAY_MAX_HEIGHT_PX,
  BANNER_MAX_CONTENT_WIDTH_PX,
} from "./cropImageFile";

/** Tells the browser the image renders at most the shell width. */
export const BANNER_IMG_SIZES = `min(100%, ${BANNER_MAX_CONTENT_WIDTH_PX}px)`;

const BANNER_DELIVERY_QUALITY = 92;

function isTransformableStorageUrl(url: string): boolean {
  if (!url || typeof url !== "string") return false;
  return toSupabaseTransformableUrl(url).includes(
    "/storage/v1/render/image/",
  );
}

/**
 * Delivery width for a given device-pixel ratio.
 * Never requests above export resolution (no faux upscaling from CDN).
 */
export function getBannerDeliveryWidth(dpr = 1): number {
  const physical = Math.ceil(BANNER_MAX_CONTENT_WIDTH_PX * Math.max(1, dpr));
  return Math.min(BANNER_EXPORT_WIDTH, physical);
}

export type BannerImageSources = {
  src: string;
  srcSet?: string;
  sizes: string;
};

/**
 * Builds src / srcSet for sharp rendering on 1x and Retina without CSS upscaling.
 * Width transforms use resize=contain (via convertToWebP) so aspect is preserved.
 */
export function getBannerImageSources(url: string): BannerImageSources {
  const sizes = BANNER_IMG_SIZES;

  if (!url || !isTransformableStorageUrl(url)) {
    return { src: url, sizes };
  }

  const width1x = getBannerDeliveryWidth(1);
  const width2x = getBannerDeliveryWidth(2);

  const src = convertToWebP(url, width1x, undefined, BANNER_DELIVERY_QUALITY);
  const retina = convertToWebP(url, width2x, undefined, BANNER_DELIVERY_QUALITY);

  if (width1x === width2x) {
    return { src, sizes };
  }

  return {
    src,
    srcSet: `${src} ${width1x}w, ${retina} ${width2x}w`,
    sizes,
  };
}

export { BANNER_DISPLAY_MAX_HEIGHT_PX, BANNER_MAX_CONTENT_WIDTH_PX };

export function getBannerOriginalImageUrl(component: {
  original_banner_image?: string | null;
  details?: string | null;
}): string {
  const explicit = String(component?.original_banner_image ?? "").trim();
  if (explicit) {
    return explicit;
  }

  return String(component?.details ?? "").split(",")[0]?.trim() || "";
}

/** True when the banner display image was positioned via the image cropper. */
export function hasBannerCropDimensions(component: {
  original_banner_image?: string | null;
  details?: string | null;
}): boolean {
  const displayUrl = String(component?.details ?? "").split(",")[0]?.trim();
  const originalUrl = String(component?.original_banner_image ?? "").trim();

  if (!displayUrl || !originalUrl) {
    return false;
  }

  return originalUrl !== displayUrl;
}

export function getBannerImageClassName(
  component: {
    original_banner_image?: string | null;
    details?: string | null;
  },
  extraClasses = ""
): string {
  const classes = [
    "banner-image",
    hasBannerCropDimensions(component) ? "" : "banner-image--legacy",
    extraClasses,
  ].filter(Boolean);

  return classes.join(" ");
}
